import { lazy, ComponentType } from "react";

/**
 * lazyWithRetry — Envoltorio de React.lazy con reintento automático y recarga de página ante fallos
 * por despliegue de nueva versión (chunks con hash obsoleto) o caídas de red transitorias.
 *
 * Si un chunk estático ya no existe en el servidor (HTTP 404) tras una subida de release:
 * 1. Intenta reintentar la importación una vez tras una breve espera.
 * 2. Si falla y no se ha forzado recarga recientemente, limpia la caché y recarga window.location.
 * 3. Si la recarga ya ocurrió y sigue fallando, lanza el error para que el ErrorBoundary lo capture.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    const pageHasBeenForceRefreshed = sessionStorage.getItem("brickshare_chunk_reload") === "true";

    try {
      return await componentImport();
    } catch (error: any) {
      // Si el error es de tipo chunk load / network / dynamic import failure / MIME type HTML por SPA fallback
      const isChunkError =
        error?.name === "ChunkLoadError" ||
        /Failed to fetch dynamically imported module/i.test(error?.message || "") ||
        /Loading chunk/i.test(error?.message || "") ||
        /Importing a module script failed/i.test(error?.message || "") ||
        /is not a valid JavaScript MIME type/i.test(error?.message || "") ||
        /text\/html.*MIME type/i.test(error?.message || "");

      if (isChunkError && !pageHasBeenForceRefreshed) {
        sessionStorage.setItem("brickshare_chunk_reload", "true");
        window.location.reload();
        // Devolvemos una promesa que nunca resuelve mientras el navegador se recarga
        return new Promise<{ default: T }>(() => {});
      }

      // Si no es error de chunk o ya se recargó sin éxito, resetear la bandera y lanzar el error
      sessionStorage.removeItem("brickshare_chunk_reload");
      throw error;
    }
  });
}
